import React, { PropsWithChildren, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import Header from './Header';
import Footer from './Footer';
import { UseLayoutProps } from './types';
import { LAYOUT_VARIABLES } from './constants';

import { useAnimationScrollSuspend } from 'animations/hooks';
import { Classname } from 'utils/types';

export type TLayout = ReturnType<UseLayoutProps> & Classname & PropsWithChildren;

export enum LAYOUT_AREAS {
  HEADER = 'header',
  CONTENT = 'content',
  FOOTER = 'footer',
}

const Wrapper = styled.div`
  ${({ theme: { colors, mq } }) => css`
    ${LAYOUT_VARIABLES.headerVerticalPadding}: 3rem;
    ${LAYOUT_VARIABLES.headerLogoHeight}: 6rem;
    ${LAYOUT_VARIABLES.headerHeight}: calc(var(${LAYOUT_VARIABLES.headerVerticalPadding}) * 2 + var(${LAYOUT_VARIABLES.headerLogoHeight}));

    display: grid;
    position: relative;
    grid-template-areas:
      '${LAYOUT_AREAS.HEADER}'
      '${LAYOUT_AREAS.CONTENT}'
      '${LAYOUT_AREAS.FOOTER}';
    grid-template-rows: min-content 1fr min-content;
    background-color: ${colors.white};
    width: 100%;
    min-height: 100vh;

    ${mq.md} {
      ${LAYOUT_VARIABLES.headerVerticalPadding}: 1.3125rem;
      ${LAYOUT_VARIABLES.headerLogoHeight}: 4.125rem;
    }
  `}
`;

const TaggedHeader = styled(Header)`
  grid-area: header;
`;

const Content = styled.main`
  grid-area: content;
`;

const TaggedFooter = styled(Footer)`
  grid-area: footer;
`;

const Layout: React.FC<TLayout> = ({
  children,
  className,
  isDark,
  currentLanguage,
  alternateLanguages,
  fallbackUrls,
  prependFooterLinks,
  footerLinkRows,
}) => {
  useLayoutEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 0);
  }, []);

  useAnimationScrollSuspend();

  return (
    <Wrapper className={className}>
      <TaggedHeader
        isDark={isDark}
        currentLanguage={currentLanguage}
        alternateLanguages={alternateLanguages}
        fallbackUrls={fallbackUrls}
      />
      <Content>{children}</Content>
      <TaggedFooter
        currentLanguage={currentLanguage}
        prependFooterLinks={prependFooterLinks}
        footerLinkRows={footerLinkRows}
      />
    </Wrapper>
  );
};

export default Layout;
